// PLC Error Guide - 앱 전체 라우팅 및 안드로이드 백버튼 처리
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { App as CapacitorApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import i18n from './i18n';

// 글로벌 컨텍스트 & 레이아웃 컴포넌트
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import Header from './components/layout/Header';
import { AuthProvider } from './contexts/AuthContext';
import AdMobBanner from './components/AdMobBanner';
import ContactDeveloperFab from './components/contact/ContactDeveloperFab';

// 페이지 lazy loading
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductCategoryPage = lazy(() => import('./pages/ProductCategoryPage'));
const ErrorSearchPage = lazy(() => import('./pages/ErrorSearchPage'));
const ErrorDetailPage = lazy(() => import('./pages/ErrorDetailPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// 로딩 스피너
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const exitDialogRef = useRef(exitDialogOpen);

  useEffect(() => {
    exitDialogRef.current = exitDialogOpen;
  }, [exitDialogOpen]);

  // 시스템 언어 감지 및 강제 설정
  useEffect(() => {
    const setSystemLanguage = async () => {
      try {
        const info = await Device.getLanguageCode();
        const systemLang = info.value.split('-')[0]; // 'ko-KR' → 'ko'

        console.log('[Language Debug]');
        console.log('navigator.language:', navigator.language);
        console.log('Device.getLanguageCode():', info.value);
        console.log('Extracted lang:', systemLang);
        console.log('Current i18n lang:', i18n.language);

        // 지원하는 언어인지 확인
        const supportedLangs = ['ko', 'en', 'zh', 'ja', 'pt', 'ar'];
        const targetLang = supportedLangs.includes(systemLang) ? systemLang : 'ko';

        // 현재 언어와 다르면 변경
        if (i18n.language !== targetLang) {
          await i18n.changeLanguage(targetLang);
          console.log('Language changed to:', targetLang);
        }
      } catch (error) {
        console.error('Failed to get system language:', error);
        // 웹 환경 fallback: navigator 사용
        const browserLang = navigator.language.split('-')[0];
        const supportedLangs = ['ko', 'en', 'zh', 'ja', 'pt', 'ar'];
        const targetLang = supportedLangs.includes(browserLang) ? browserLang : 'ko';
        if (i18n.language !== targetLang) {
          i18n.changeLanguage(targetLang);
        }
      }
    };

    setSystemLanguage();
  }, []);

  const handleBackButton = useCallback(() => {
    const historyState = window.history.state;
    const historyIndex = typeof historyState?.idx === 'number' ? historyState.idx : 0;

    if (exitDialogRef.current) {
      try {
        CapacitorApp.exitApp();
      } catch (error) {
        // 웹 환경에서는 무시
      }
      return;
    }

    if (historyIndex > 0) {
      navigate(-1);
    } else {
      exitDialogRef.current = true;
      setExitDialogOpen(true);
    }
  }, [navigate]);

  useEffect(() => {
    const listenerPromise = CapacitorApp.addListener('backButton', handleBackButton);
    return () => {
      listenerPromise?.then((listener) => listener.remove());
    };
  }, [handleBackButton]);

  const handleCancelExit = useCallback(() => {
    exitDialogRef.current = false;
    setExitDialogOpen(false);
  }, []);

  const handleConfirmExit = useCallback(() => {
    exitDialogRef.current = false;
    try {
      CapacitorApp.exitApp();
    } catch (error) {
      // 웹 환경에서는 무시
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/manufacturer/:manufacturerId" element={<ProductCategoryPage />} />
          <Route path="/manufacturer/:manufacturerId/:productCategory" element={<ErrorSearchPage />} />
          <Route
            path="/manufacturer/:manufacturerId/:productCategory/error/:errorCode/:docId"
            element={<ErrorDetailPage />}
          />
        </Routes>
      </Suspense>

      {/* 개발자에게 문의하기 플로팅 버튼 */}
      <ContactDeveloperFab />

      {/* 모든 페이지 하단 광고 (AdMob만 사용 - 앱 전용) */}
      {/* Android 15 edge-to-edge: 하단 safe-area-inset 적용 */}
      <Box
        sx={{
          py: 4,
          paddingBottom: 'calc(1rem + var(--safe-area-inset-bottom, 0px))',
        }}
      >
        <AdMobBanner />
      </Box>

      <Dialog
        open={exitDialogOpen}
        onClose={handleCancelExit}
        aria-labelledby="app-exit-dialog-title"
        aria-describedby="app-exit-dialog-description"
      >
        <DialogTitle id="app-exit-dialog-title">{t('exitDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="app-exit-dialog-description">
            {t('exitDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExit}>{t('exitDialog.cancel')}</Button>
          <Button onClick={handleConfirmExit} color="error" variant="contained">
            {t('exitDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
